import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, LayoutDashboard, TrendingUp, TrendingDown, FileText } from "lucide-react";
import { canSee } from "@/lib/menus";
import { usePabrik, GABUNGAN } from "@/context/PabrikContext";
import { buildQuickAccess } from "@/components/app/quickAccess";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const MobileTab = ({ to, icon: Icon, label, testid, match }) => {
  const location = useLocation();
  const active = match ? match.some((p) => location.pathname.startsWith(p)) : location.pathname === to;
  return (
    <NavLink to={to} data-testid={testid}
      className={`mobile-tab ${active ? "active" : "text-slate-500"} flex flex-col items-center gap-1 py-2 px-1 flex-1 min-w-0`}>
      <span className="mobile-tab-icon h-9 w-12 rounded-full flex items-center justify-center"><Icon size={18} /></span>
      <span className="text-[10px] font-medium truncate">{label}</span>
    </NavLink>
  );
};

const QuickCard = ({ item, onPick }) => {
  const Icon = item.icon;
  return (
    <button type="button" onClick={() => onPick(item)} data-testid={`qa-item-${slug(item.label)}`}
      className="qa-card text-left rounded-2xl bg-slate-50 border border-slate-100 p-3.5 flex flex-col gap-2.5 min-h-[88px]">
      <span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}><Icon size={16} /></span>
      <span>
        <span className="block text-[13px] font-semibold text-slate-800 leading-tight">{item.label}</span>
        <span className="block text-[10px] uppercase tracking-wide text-slate-400 mt-1">{item.sub}</span>
      </span>
    </button>
  );
};

function QuickSheet({ sections, onClose, onPick }) {
  return (
    <>
      <motion.div key="qa-overlay" data-testid="qa-overlay" onPointerDown={onClose} onClick={onClose}
        className="md:hidden fixed inset-0 z-[35] bg-slate-900/30 backdrop-blur-md"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} />
      <motion.div key="qa-sheet" data-testid="quick-access-sheet" role="dialog" aria-label="Quick Access"
        className="md:hidden fixed inset-x-0 bottom-0 z-[35] bg-white rounded-t-[28px] shadow-2xl max-h-[78dvh] flex flex-col"
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        drag="y" dragConstraints={{ top: 0, bottom: 0 }} dragElastic={{ top: 0, bottom: 0.6 }}
        onDragEnd={(_, i) => { if (i.offset.y > 100 || i.velocity.y > 500) onClose(); }}>
        <div className="pt-3 pb-1 flex justify-center shrink-0 cursor-grab"><span className="h-1.5 w-10 rounded-full bg-slate-200" /></div>
        <div className="px-5 pt-2 pb-3 shrink-0">
          <h2 className="text-lg font-bold text-slate-900">Quick Access</h2>
          <p className="text-xs text-slate-500">Aksi cepat untuk mencatat dan mengelola data Anda.</p>
        </div>
        <div className="flex-1 overflow-y-auto px-5 qa-scroll" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" }}
          onPointerDownCapture={(e) => e.stopPropagation()}>
          {sections.map((s, si) => (
            <motion.section key={s.title} className="mb-4" data-testid={`qa-section-${slug(s.title)}`}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + si * 0.05 }}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{s.title}</div>
              <div className="grid grid-cols-2 gap-2.5">
                {s.items.map((item) => <QuickCard key={item.to} item={item} onPick={onPick} />)}
              </div>
            </motion.section>
          ))}
          {sections.length === 0 && <p className="text-sm text-slate-500 py-6 text-center" data-testid="qa-empty">Belum ada menu yang bisa diakses.</p>}
        </div>
      </motion.div>
    </>
  );
}

export function MobileQuickNav({ user }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const pabrik = usePabrik();
  const cur = pabrik.enabled ? pabrik.current : null;
  const isGab = cur?.id === GABUNGAN;
  const sections = buildQuickAccess(user, { jenis: cur?.jenis, isGab, targetsBriket: pabrik.targetsBriket });

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const toggle = () => {
    try { navigator.vibrate?.(open ? 8 : 15); } catch { /* not supported */ }
    setOpen((o) => !o);
  };

  const pick = (item) => {
    if (item.add && isGab) { setOpen(false); toast.error("Mode Gabungan hanya untuk melihat. Pilih salah satu pabrik untuk menambah data."); return; }
    setOpen(false);
    nav(item.to);
  };

  const tabs = [
    canSee(user, "dashboard") && { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "mtab-dashboard" },
    canSee(user, "pemasukan") && { to: "/pemasukan", icon: TrendingUp, label: "Pemasukan", testid: "mtab-pemasukan", match: ["/pemasukan"] },
    canSee(user, "pengeluaran") && { to: "/pengeluaran", icon: TrendingDown, label: "Pengeluaran", testid: "mtab-pengeluaran", match: ["/pengeluaran"] },
    canSee(user, "laporan_semua") && { to: "/laporan/semua", icon: FileText, label: "Laporan", testid: "mtab-laporan", match: ["/laporan"] },
  ].filter(Boolean);
  const half = Math.ceil(tabs.length / 2);

  return (
    <>
      <AnimatePresence>{open && <QuickSheet sections={sections} onClose={() => setOpen(false)} onPick={pick} />}</AnimatePresence>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-200 z-40 flex items-end pwa-safe-bottom shadow-[0_-6px_20px_-12px_rgba(15,23,42,0.25)]" data-testid="mobile-bottom-nav">
        {tabs.slice(0, half).map((t) => <MobileTab key={t.to} {...t} />)}
        <div className="flex-1 relative self-stretch min-w-0">
          <button type="button" onClick={toggle} data-testid="mobile-quick-fab"
            aria-label={open ? "Tutup Quick Access" : "Buka Quick Access"} aria-expanded={open}
            className="qa-fab absolute left-1/2 -ml-[33px] -top-7 h-[66px] w-[66px] rounded-full text-white flex items-center justify-center ring-[6px] ring-white">
            <Plus size={30} strokeWidth={2.5} className={`transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
          </button>
        </div>
        {tabs.slice(half).map((t) => <MobileTab key={t.to} {...t} />)}
      </nav>
    </>
  );
}
